if (!customElements.get('lookbook-slideshow')) {
  customElements.define(
    'lookbook-slideshow',
    class LookbookSlideshow extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-lookbook-track]');
        this.dotsContainer = this.querySelector('[data-lookbook-dots]');
        this.realSlides = Array.from(this.querySelectorAll('[data-lookbook-slide]:not([data-lookbook-clone])'));

        if (!this.track || this.realSlides.length < 2) return;

        // Seamless loop: clone the first/last slides onto the opposite ends, then
        // silently jump back to the identical real slide whenever the scroll settles
        // inside a clone region. Cloning two per side covers up to two-up layouts.
        this.buildClones(2);
        this.slides = Array.from(this.track.querySelectorAll('[data-lookbook-slide]'));

        this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
        this.track.addEventListener('scrollend', () => this.settle());

        // Mouse drag-to-scroll. Touch, pen and trackpad already scroll the track
        // natively (and loop via settle()), so we only take over for the mouse.
        this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.track.addEventListener('dragstart', (event) => event.preventDefault());

        this.buildDots();
        this.updateDraggable();
        this.anchorToPage(0); // start on the first real page (past the leading clones)

        this.resizeObserver = new ResizeObserver(() => this.scheduleBuild());
        this.resizeObserver.observe(this.track);
        this.breakpoint = window.matchMedia('(min-width: 750px)');
        this.onBreakpoint = () => this.scheduleBuild();
        this.breakpoint.addEventListener('change', this.onBreakpoint);
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.breakpoint) this.breakpoint.removeEventListener('change', this.onBreakpoint);
        cancelAnimationFrame(this.buildRaf);
        cancelAnimationFrame(this.animRaf);
        clearTimeout(this.settleTimer);
        this.endDrag();
      }

      buildClones(perSide) {
        this.track.querySelectorAll('[data-lookbook-clone]').forEach((clone) => clone.remove());
        const n = this.realSlides.length;
        this.clones = Math.min(perSide, n);

        const leading = document.createDocumentFragment();
        for (let i = n - this.clones; i < n; i++) leading.appendChild(this.makeClone(this.realSlides[i]));
        this.track.insertBefore(leading, this.track.firstChild);

        const trailing = document.createDocumentFragment();
        for (let i = 0; i < this.clones; i++) trailing.appendChild(this.makeClone(this.realSlides[i]));
        this.track.appendChild(trailing);
      }

      makeClone(slide) {
        const clone = slide.cloneNode(true);
        clone.setAttribute('data-lookbook-clone', '');
        clone.removeAttribute('id');
        clone.setAttribute('aria-hidden', 'true');
        // Keep clones out of the tab order / accessibility tree.
        clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
        return clone;
      }

      // Distance between adjacent slide starts (slide width, since there is no gap).
      slideStep() {
        const step = this.slides[1].offsetLeft - this.slides[0].offsetLeft;
        return step > 0 ? step : this.track.clientWidth;
      }

      slidesPerPage() {
        return Math.max(1, Math.round(this.track.clientWidth / this.slideStep()));
      }

      // First real-slide index of each page, with the last page clamped to the end so
      // every page is a full group (e.g. [0, 2, 3] for 5 slides two-up).
      pageStarts() {
        const n = this.realSlides.length;
        const per = this.slidesPerPage();
        const starts = [];
        for (let i = 0; i < n; i += per) starts.push(Math.min(i, n - per));
        return starts.filter((value, index) => index === 0 || value !== starts[index - 1]);
      }

      updateDraggable() {
        this.track.classList.toggle('is-draggable', this.track.scrollWidth > this.track.clientWidth + 1);
      }

      // Rebuild dots + re-anchor after the browser lays out the new size. Clones are
      // fixed (first/last slides) so they never need rebuilding on resize.
      scheduleBuild() {
        cancelAnimationFrame(this.buildRaf);
        this.buildRaf = requestAnimationFrame(() => {
          this.buildDots();
          this.updateDraggable();
          this.anchorToPage(this.activePage || 0);
        });
      }

      buildDots() {
        if (!this.dotsContainer) return;
        const starts = this.pageStarts();

        if (this.dotsContainer.childElementCount !== starts.length) {
          this.dotsContainer.textContent = '';
          starts.forEach((slideIndex, page) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'slider-counter__link slider-counter__link--dots';
            button.setAttribute('aria-label', `Go to slide ${page + 1} of ${starts.length}`);
            const dot = document.createElement('span');
            dot.className = 'dot';
            button.appendChild(dot);
            button.addEventListener('click', () => this.goToPage(page));
            this.dotsContainer.appendChild(button);
          });
        }

        this.dots = Array.from(this.dotsContainer.children);
        this.setActivePage(this.currentPage());
      }

      // Scroll position (in px) that shows real-slide index `realIndex` at the left.
      offsetForReal(realIndex) {
        return this.slides[this.clones + realIndex].offsetLeft - this.track.offsetLeft;
      }

      anchorToPage(page) {
        const starts = this.pageStarts();
        const p = Math.max(0, Math.min(page, starts.length - 1));
        this.track.scrollLeft = this.offsetForReal(starts[p]);
        this.setActivePage(p);
      }

      goToPage(page) {
        const starts = this.pageStarts();
        const p = Math.max(0, Math.min(page, starts.length - 1));
        this.setActivePage(p);
        this.animateScrollTo(this.offsetForReal(starts[p]));
      }

      // Advance/retreat one page, looping seamlessly at the ends by sliding into the
      // clones and then normalizing back to the real slides.
      step(direction) {
        const starts = this.pageStarts();
        const pages = starts.length;
        const per = this.slidesPerPage();
        const n = this.realSlides.length;
        const from = this.dragStartPage;
        let target;

        if (direction > 0 && from === pages - 1) {
          // Wrap forward: slide into the trailing clones (which show page 0).
          target = this.slides[this.clones + n].offsetLeft - this.track.offsetLeft;
          this.setActivePage(0);
        } else if (direction < 0 && from === 0) {
          // Wrap backward: slide into the leading clones (which show the last page).
          target = this.slides[this.clones - per].offsetLeft - this.track.offsetLeft;
          this.setActivePage(pages - 1);
        } else {
          const p = Math.max(0, Math.min(from + direction, pages - 1));
          this.setActivePage(p);
          target = this.offsetForReal(starts[p]);
        }
        this.animateScrollTo(target);
      }

      // Slide to a scroll position with easing. We drive scrollLeft frame-by-frame
      // rather than scrollTo({behavior:'smooth'}) because a smooth programmatic scroll
      // fights the mandatory scroll-snap. Snapping is off for the duration and restored
      // at the end; then normalizeLoop() silently repositions out of any clone region.
      animateScrollTo(target) {
        cancelAnimationFrame(this.animRaf);
        const start = this.track.scrollLeft;
        const distance = target - start;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const finish = () => {
          this.isAnimating = false;
          this.track.style.scrollSnapType = '';
          this.normalizeLoop();
          this.setActivePage(this.currentPage());
        };

        if (reduceMotion || Math.abs(distance) < 1) {
          this.track.scrollLeft = target;
          finish();
          return;
        }

        this.isAnimating = true;
        this.track.style.scrollSnapType = 'none';
        const duration = 450;
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
        let startTime = null;

        const frame = (now) => {
          if (startTime === null) startTime = now;
          const progress = Math.min((now - startTime) / duration, 1);
          this.track.scrollLeft = start + distance * easeOutCubic(progress);
          if (progress < 1) {
            this.animRaf = requestAnimationFrame(frame);
          } else {
            this.track.scrollLeft = target;
            finish();
          }
        };
        this.animRaf = requestAnimationFrame(frame);
      }

      // If the scroll has settled inside a clone region, jump to the identical real
      // slide. Same visual, so the reposition is invisible — this is what makes the
      // loop seamless.
      normalizeLoop() {
        if (this.isDragging || this.isAnimating) return;
        const step = this.slideStep();
        const snapped = Math.round(this.track.scrollLeft / step);
        const n = this.realSlides.length;
        if (snapped < this.clones) {
          this.track.scrollLeft += n * step;
        } else if (snapped >= this.clones + n) {
          this.track.scrollLeft -= n * step;
        }
      }

      onScroll() {
        this.setActivePage(this.currentPage());
        if (this.isDragging || this.isAnimating) return;
        // Fallback for browsers without scrollend: normalize once scrolling goes idle.
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 120);
      }

      settle() {
        if (this.isDragging || this.isAnimating) return;
        this.normalizeLoop();
      }

      // ----- mouse drag to scroll -----

      onPointerDown(event) {
        if (event.pointerType !== 'mouse') return; // touch & pen scroll natively
        cancelAnimationFrame(this.animRaf);
        this.isDragging = true;
        this.dragMoved = false;
        this.dragStartX = event.clientX;
        this.dragStartScroll = this.track.scrollLeft;
        this.dragStartPage = this.currentPage();
        this.track.style.scrollSnapType = 'none';
        this.track.classList.add('is-dragging');

        this.onDragMove = (moveEvent) => this.onPointerMove(moveEvent);
        this.onDragUp = (upEvent) => this.onPointerUp(upEvent);
        document.addEventListener('pointermove', this.onDragMove);
        document.addEventListener('pointerup', this.onDragUp);
        document.addEventListener('pointercancel', this.onDragUp);
      }

      onPointerMove(event) {
        if (!this.isDragging) return;
        const dx = event.clientX - this.dragStartX;
        if (Math.abs(dx) > 5) this.dragMoved = true;
        this.track.scrollLeft = this.dragStartScroll - dx;
      }

      onPointerUp() {
        if (!this.isDragging) return;
        const dragged = this.track.scrollLeft - this.dragStartScroll;
        this.endDrag();

        const threshold = Math.min(this.track.clientWidth * 0.15, 80);
        if (dragged > threshold) this.step(1);
        else if (dragged < -threshold) this.step(-1);
        else this.step(0);

        // Swallow the click that follows a real drag so image links don't fire.
        if (this.dragMoved) {
          const suppressClick = (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
          };
          this.track.addEventListener('click', suppressClick, { capture: true, once: true });
        }
      }

      endDrag() {
        this.isDragging = false;
        this.track.classList.remove('is-dragging');
        if (this.onDragMove) document.removeEventListener('pointermove', this.onDragMove);
        if (this.onDragUp) {
          document.removeEventListener('pointerup', this.onDragUp);
          document.removeEventListener('pointercancel', this.onDragUp);
        }
      }

      // Real-slide index currently at the left edge (mapping clone regions back to real).
      currentRealSlide() {
        const step = this.slideStep();
        let snapped = Math.round(this.track.scrollLeft / step);
        const n = this.realSlides.length;
        if (snapped < this.clones) snapped += n;
        else if (snapped >= this.clones + n) snapped -= n;
        return snapped - this.clones;
      }

      // Nearest page to the current position.
      currentPage() {
        const real = this.currentRealSlide();
        const starts = this.pageStarts();
        let best = 0;
        let smallest = Infinity;
        starts.forEach((start, index) => {
          const distance = Math.abs(start - real);
          if (distance < smallest) {
            smallest = distance;
            best = index;
          }
        });
        return best;
      }

      setActivePage(page) {
        this.activePage = page;
        if (!this.dots) return;
        this.dots.forEach((dot, index) => {
          dot.classList.toggle('slider-counter__link--active', index === page);
          if (index === page) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });
      }
    }
  );
}
